import React from "react";
import { Box, Button, DrawerBody, DrawerHeader, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import { Tooltip } from "@familyzone/component-library";
import { LibraryDrawerView } from ".";
import { colours } from "@/utils/themeUtils";

import emptyInbox from "@/assets/emptyInbox.svg?url";
import { FocusesList } from "./FocusesList";
import { FocusDTO } from "@/js/api/focus";
import { EditingFocus } from "./FocusPage";
import { getSignaturesForFocusResourcePicker, transformFocusTabDTOToSelectedResource } from "./FocusResourcePicker/utils";
import useStore from "@/stores/index";
import { useFocusStore } from "@/stores/v2/focusStore";
import { envConfig } from "@/config";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { ChakraFAIcon } from "@/components/ui/Icon";

interface Props {
	jumpToView: (view: LibraryDrawerView) => void;
	setEditingFocus: (focus: EditingFocus) => void;
	closeDrawer: () => void;
}

export function LibraryPage(props: Props): JSX.Element {
	const MAX_FOCUSES_IN_LIBRARY = envConfig.MAX_FOCUSES_IN_LIBRARY;

	const store = useStore();
	const focusStore = useFocusStore();
	const selectedClassroomStore = useSelectedClassroomStore();

	function handleClickFocusResource(focus: FocusDTO) {
		const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(store.signatures);

		props.setEditingFocus({
			id: focus.id,
			name: focus.name,
			active: focus.active,
			resources: focus.focusTabs.map((resource) => transformFocusTabDTOToSelectedResource(resource, allSignatureResourceOptions)),
			isOnTheFly: !focus.name,
			studentIds: focus.studentIds || [],
		});

		props.jumpToView(LibraryDrawerView.FocusView);
	}

	return (
		<>
			<DrawerHeader bg={colours.gray[100]} p={7}>
				<Flex justifyContent={"space-between"}>
					<Text fontSize={"xl"} fontWeight={"medium"} mb={3}>
						Focus Library
					</Text>
				</Flex>
				<Flex justifyContent={"space-between"}>
					<Text color={"neutrals.300"} mr={5} fontSize={"md"} fontWeight={400}>
						Create a focus. Add resources and save it to your library. (Library max {MAX_FOCUSES_IN_LIBRARY} focus plans).
					</Text>
					<Box>
						<Tooltip
							label={`Maximum limit of ${MAX_FOCUSES_IN_LIBRARY} focus plans`}
							isDisabled={focusStore.focusLibrary.length < MAX_FOCUSES_IN_LIBRARY}
						>
							<Button
								onClick={() => props.jumpToView(LibraryDrawerView.FocusView)}
								isDisabled={focusStore.focusLibrary.length >= MAX_FOCUSES_IN_LIBRARY && !selectedClassroomStore.stableIdV1}
							>
								<ChakraFAIcon icon={"fa-arrows-to-eye"} variant="solid" mr={2} boxSize={4} />
								Create Focus
							</Button>
						</Tooltip>
					</Box>
				</Flex>
			</DrawerHeader>
			<DrawerBody pt={3} pb={0} px={3}>
				{focusStore.focusLibrary.length === 0 ? (
					<Box textAlign={"center"} mt={150}>
						{focusStore.fetchingLibraryItems ? (
							<Spinner />
						) : (
							<Box>
								<Image src={emptyInbox} alt={"Create your first Focus."} mx={"auto"} mb={20} />
								<Text fontSize={"sm"} color={"neutrals.300"}>
									Create your first Focus.
								</Text>
								<Text fontSize={"sm"} color={"neutrals.300"}>
									It can be used in any of your classes
								</Text>
							</Box>
						)}
					</Box>
				) : (
					<FocusesList handleClickFocusResource={handleClickFocusResource} hideDeleteOption={!!selectedClassroomStore.stableIdV1} />
				)}
			</DrawerBody>
		</>
	);
}
